import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getOptionsGlobal } from 'actions/global';
import { mapDataOptions } from 'utils/helpers';
import isEqual from 'lodash/isEqual';
import { filterVisiblePurchaseRequisitionTypes } from 'utils/purchaseRequisitionType';

const ACTIVE_ONLY_ORG_OPTION_TYPES = ['company', 'business', 'cluster', 'store'];

const sortObjectKeysDeep = (value) => {
  if (Array.isArray(value)) {
    return value.map(sortObjectKeysDeep);
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((result, key) => {
        result[key] = sortObjectKeysDeep(value[key]);
        return result;
      }, {});
  }

  return value;
};

const normalizePurchaseRequisitionSortValue = (option = {}) => {
  const rawDate =
    option?.purchase_requisition_date ||
    option?.purchaserequisitiondate ||
    option?.created_date ||
    option?.createddate ||
    null;

  if (rawDate && typeof rawDate === 'string') {
    const dateMatch = rawDate.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (dateMatch) {
      return Number(`${dateMatch[3]}${dateMatch[2]}${dateMatch[1]}`);
    }
  }

  const rawCode = option?.code || option?.name || option?.label || option?.value || '';
  const normalizedCode = String(rawCode).trim().toUpperCase();
  const numericPart = normalizedCode.replace(/\D/g, '');

  if (numericPart) {
    return Number(numericPart);
  }

  return normalizedCode;
};

const sortOptionsByNewest = (optionName, options = []) => {
  if (optionName !== 'purchaseRequisition') {
    return options;
  }

  return [...options].sort((optionA, optionB) => {
    const sortValueA = normalizePurchaseRequisitionSortValue(optionA);
    const sortValueB = normalizePurchaseRequisitionSortValue(optionB);

    if (typeof sortValueA === 'number' && typeof sortValueB === 'number') {
      return sortValueB - sortValueA;
    }

    return String(sortValueB).localeCompare(String(sortValueA), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  });
};

function useGetOptions(optionName, funcOptions = {}) {
  const dispatch = useDispatch();
  const {
    valueName = 'id',
    labelName = 'name',
    valueAsString = false,
    params = { is_active: 1 },
    checkParams = true,
    isHasOptionAll = false,
  } = funcOptions;

  const storedOptions = useSelector((state) => state.global[`${optionName}Data`]);
  const storedParams = useSelector((state) => state.global[`${optionName}Params`]);
  const normalizedParams = useMemo(() => {
    const nextParams = params ? { ...params } : {};

    if (ACTIVE_ONLY_ORG_OPTION_TYPES.includes(optionName) && nextParams.is_active === undefined) {
      nextParams.is_active = 1;
    }

    return nextParams;
  }, [optionName, params]);

  const cacheKey = useMemo(
    () => `${optionName}:${JSON.stringify(sortObjectKeysDeep(normalizedParams || {}))}`,
    [normalizedParams, optionName],
  );

  const cachedOptions = useSelector((state) => state.global[`${optionName}Cache`]?.[cacheKey]);
  const isExactFetching = useSelector((state) => state.global[`${optionName}LoadingMap`]?.[cacheKey]);

  useEffect(() => {
    if (!checkParams || isExactFetching) return;
    if (!cachedOptions) {
      dispatch(getOptionsGlobal(optionName, normalizedParams)).catch(() => {});
    }
  }, [cachedOptions, checkParams, dispatch, isExactFetching, normalizedParams, optionName]);

  const options = useMemo(() => {
    const optionSource =
      cachedOptions || (isEqual(storedParams, normalizedParams) ? storedOptions : undefined);

    const normalizedOptions =
      optionSource && isHasOptionAll ? [{ id: 0, name: 'Tất cả' }, ...optionSource] : optionSource;

    const visibleOptions =
      optionName === 'purchaseRequisitionType'
        ? filterVisiblePurchaseRequisitionTypes(normalizedOptions || [])
        : normalizedOptions;

    return sortOptionsByNewest(optionName, mapDataOptions(visibleOptions, { valueName, labelName, valueAsString }));
  }, [cachedOptions, isHasOptionAll, labelName, normalizedParams, optionName, storedOptions, storedParams, valueAsString, valueName]);

  return options?.length ? options : [];
}

/**
 * this list ref to: api-portal/app/modules/global/utils/constants.js
 */
export const optionType = {
  supplier: 'supplier',
  purchaseRequisition: 'purchaseRequisition',
  store: 'store',
  company: 'company',
  department: 'department',
  purchaseOrder: 'purchaseOrder',
  business: 'business',
  area: 'area',
  taskWorkFlow: 'taskWorkFlow',
  productModel: 'productModel',
  conditionDb: 'conditionDb',
  source: 'source',
  customerType: 'customerType',
  poReviewLevel: 'poReviewLevel',
  user: 'user',
  discountProgram: 'discountProgram',
  poDivisionReviewLevel: 'poDivisionReviewLevel',
  taskType: 'taskType',
  taskTypeAuto: 'taskTypeAuto',
  order: 'order',
  stocksInType: 'stocksInType',
  stocksCompany: 'stocksCompany',
  bank: 'bank',
  province: 'province',
  block: 'block',
  hobbiesForUser: 'hobbiesForUser',
  cluster: 'cluster',
  zaloTemplate: 'zaloTemplate',
  expendType: 'expendType',
  purchaseRequisitionType: 'purchaseRequisitionType',
  storeType: 'storeType',
  brand: 'brand',
  taskTypeWflow: 'taskTypeWflow',
  requestPurchaseOrder: 'requestPurchaseOrder',
  positionByDepartment: 'positionByDepartment',
  interestContent: 'interestContent',
  stocksType: 'stocksType',
  documentType: 'documentType',
  timeKeepingClaimType: 'timeKeepingClaimType',
  shift: 'shift',
  accounting_account: 'accounting_account',
  accountingAccount: 'accounting_account',
  internalTransferType: 'internalTransferType',
  storeByBusiness: 'storeByBusiness',
  stocks: 'stocks',
  stockTypeByStore: 'stockTypeByStore',
  paymentForm: 'paymentForm',
};

export default useGetOptions;
